
import { useEffect, useMemo, useState } from 'react';

export type StorageType = 'localStorage' | 'sessionStorage';

const win = typeof window !== 'undefined' ? window : global;

const storage = {
  getItem(type: StorageType, key: string) {
    if (typeof window === 'undefined') return null;
    try {
      const item = win[type].getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Failed to parse ${type} item for key ${key}`, error);
      return null;
    }
  },
  removeItem(type: StorageType, key: string) {
    if (typeof window === 'undefined') return;
    win[type].removeItem(key);
  },
  setItem(type: StorageType, key: string, value: any) {
    if (typeof window === 'undefined') return;
    win[type].setItem(key, JSON.stringify(value));
  },
};

/**
 * Hook for managing local or session storage values
 * @param {string} key The storage key to manage
 * @param {StorageType} type The storage type (local or session)
 * @param {any} [initialValue] An optional value to use to initialize the key if it doesn't exist
 */
export function useStorage<TItemType = any>(
  key: string,
  type: StorageType,
  initialValue?: TItemType
) {
  const [value, setValue] = useState<TItemType | null>(null);

  useEffect(() => {
    const storageValue = storage.getItem(type, key);

    if (storageValue === null && initialValue) {
      storage.setItem(type, key, initialValue);
      setValue(initialValue);
    } else {
      setValue(storageValue);
    }
  }, [key, type]);

  const methods = useMemo(
    () => ({
      remove: () => {
        storage.removeItem(type, key);
        setValue(null);
      },
      update: (value: TItemType) => {
        storage.setItem(type, key, value);
        setValue(value);
      },
    }),
    [key, type]
  );

  return {
    ...methods,
    value,
  };
}
